import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  OutlinedInput,
  Checkbox,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete
} from '@mui/material'
import { useQuery, useMutation, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_ADVANCED_MODIFIER_GROUPS, GET_RESTAURANT_ITEMS } from '../../apollo/advancedMenuQueries'
import { ATTACH_MODIFIER_GROUP_TO_CATEGORY } from '../../apollo/advancedMenuMutations'
import { getRestaurantDetail } from '../../apollo'
import AttachFileIcon from '@mui/icons-material/AttachFile'

// GraphQL Query to get restaurant with categories
const GET_RESTAURANT_CATEGORIES = gql`
  ${getRestaurantDetail}
`

/**
 * CategoryAttacher Component
 *
 * Allows bulk attachment of modifier groups to all items in a category.
 * This is a massive time saver for large menus - attach "Pizza Toppings" group
 * to all pizza items at once instead of one by one.
 *
 * Example use case:
 * - Category: "Pizzas"
 * - Groups: ["Pizza Toppings", "Crust Options", "Size Selection"]
 * - All pizza items inherit these groups
 */
const CategoryAttacher = () => {
  const { t } = useTranslation()
  const [categoryInputMode, setCategoryInputMode] = useState('existing') // 'existing' or 'new'
  const [categoryName, setCategoryName] = useState('')
  const [selectedGroups, setSelectedGroups] = useState([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const restaurantId = localStorage.getItem('restaurantId')

  // Query restaurant to get existing categories
  const { data: restaurantData, loading: categoriesLoading } = useQuery(GET_RESTAURANT_CATEGORIES, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  // Query all modifier groups
  const { data: groupsData, loading: groupsLoading } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const existingCategories = (restaurantData?.restaurant?.categories || []).slice().sort((a, b) =>
    (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))

  // Per-category item counts from the items' categoryName — the legacy
  // foods relation is always empty for advanced items.
  const { data: itemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId
  })
  const itemCountByCategory = {}
  for (const item of (itemsData?.advancedRestaurantItems || [])) {
    if (!item.categoryName) continue
    item.categoryName.split(',').forEach(c => {
      const key = c.trim().toLowerCase()
      if (key) itemCountByCategory[key] = (itemCountByCategory[key] || 0) + 1
    })
  }

  const [attachToCategory, { loading: attaching }] = useMutation(ATTACH_MODIFIER_GROUP_TO_CATEGORY, {
    onCompleted: (data) => {
      const count = data?.attachModifierGroupToCategory?.affectedCount || 0
      setSuccess(t('Successfully attached groups to {count} items in category "{category}"', { count, category: categoryName }))
      setTimeout(() => {
        setSuccess('')
        resetForm()
      }, 5000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const resetForm = () => {
    setCategoryName('')
    setSelectedGroups([])
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!categoryName.trim()) {
      setError(t('Category name is required'))
      return
    }

    if (selectedGroups.length === 0) {
      setError(t('Please select at least one modifier group'))
      return
    }

    attachToCategory({
      variables: {
        restaurantId,
        input: {
          categoryName: categoryName.trim(),
          modifierGroupIds: selectedGroups
        }
      }
    })
  }

  const availableGroups = groupsData?.advancedModifierGroups || []

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          {t('Category Bulk Attachment')}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('Attach modifier groups to all items in a category at once. Huge time saver!')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* CATEGORY INPUT MODE SELECTOR */}
        <FormControl component="fieldset" margin="normal">
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: '#344767' }}>
            {t('Select Category Method')}:
          </Typography>
          <RadioGroup
            row
            value={categoryInputMode}
            onChange={(e) => {
              setCategoryInputMode(e.target.value)
              setCategoryName('') // Reset category name when switching modes
            }}
          >
            <FormControlLabel
              value="existing"
              control={<Radio size="small" />}
              label={t('Use Existing Category')}
            />
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              label={t('Create New Category')}
            />
          </RadioGroup>
        </FormControl>

        {/* EXISTING CATEGORY DROPDOWN OR NEW CATEGORY INPUT */}
        {categoryInputMode === 'existing' ? (
          <Autocomplete
            fullWidth
            options={existingCategories}
            getOptionLabel={(option) => option.title}
            value={existingCategories.find(cat => cat.title === categoryName) || null}
            onChange={(event, newValue) => {
              setCategoryName(newValue ? newValue.title : '')
            }}
            loading={categoriesLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Select Category')}
                margin="normal"
                required
                helperText={
                  existingCategories.length > 0
                    ? t('Choose from your existing categories')
                    : t('No categories found. Switch to "Create New Category" or add categories in the Category section.')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {categoriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {option.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {itemCountByCategory[option.title.trim().toLowerCase()] || 0} {t('items')}
                  </Typography>
                </Box>
              </li>
            )}
          />
        ) : (
          <TextField
            fullWidth
            label={t('New Category Name')}
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            margin="normal"
            required
            placeholder={t('e.g., Pizzas, Burgers, Salads')}
            helperText={t('This will create a new category if it doesn\'t exist')}
          />
        )}

        {/* MODIFIER GROUPS MULTI-SELECT */}
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('Select Modifier Groups')}</InputLabel>
          <Select
            multiple
            value={selectedGroups}
            onChange={(e) => setSelectedGroups(e.target.value)}
            input={<OutlinedInput label={t('Select Modifier Groups')} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const group = availableGroups.find(g => g._id === value)
                  return (
                    <Chip
                      key={value}
                      label={group?.name || value}
                      size="small"
                      color="primary"
                    />
                  )
                })}
              </Box>
            )}
            disabled={groupsLoading}
          >
            {groupsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : availableGroups.length > 0 ? (
              availableGroups.map((group) => (
                <MenuItem key={group._id} value={group._id}>
                  <Checkbox checked={selectedGroups.indexOf(group._id) > -1} />
                  <ListItemText
                    primary={group.name}
                    secondary={`${group.selectionType} • ${group.isTemplate ? t('Template') : t('Standard')}`}
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {t('No modifier groups available. Create groups in Modifier Studio first.')}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* PREVIEW INFO */}
        {categoryName && selectedGroups.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              {t('Preview')}:
            </Typography>
            <Typography variant="body2">
              {t('This will attach {count} modifier group(s) to all items in category "{category}"', {
                count: selectedGroups.length,
                category: categoryName
              })}
            </Typography>
          </Alert>
        )}

        {/* SUBMIT BUTTON */}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={attaching}
          sx={{ mt: 3 }}
          startIcon={attaching ? <CircularProgress size={20} /> : <AttachFileIcon />}
        >
          {attaching ? t('Attaching...') : t('Attach to Category')}
        </Button>

        {/* EXAMPLE */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
            💡 {t('Example Use Case')}:
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            • {t('Category: "Pizzas"')}<br />
            • {t('Groups: "Pizza Toppings", "Crust Options", "Size Selection"')}<br />
            • {t('Result: All 20 pizza items now have these 3 modifier groups!')}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}

export default CategoryAttacher
